import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Sidebar } from './Sidebar';
import SeasonalAccents from '@/components/season/SeasonalAccents';
import { Menu, X } from 'lucide-react';
import { ForcePasswordChangeModal } from '@/components/auth/ForcePasswordChangeModal';
import { UrgentTaskGate } from '@/components/common/UrgentTaskGate';
import { ImpersonationBar } from '@/components/impersonation/ImpersonationBar';
import CalendarReconnectBanner from '@/components/common/CalendarReconnectBanner';
import Softphone from '@/components/voice/Softphone';

const LOGO_DARK_BG = "https://customer-assets-v7afamib.emergentagent.net/job_24da4b5c-f7b8-4285-a82c-9685a2a83611/artifacts/ej6yteqo_logo%20para%20fondo%20oscuro%20clear.webp";

export const MainLayout = () => {
  const { isAuthenticated, loading } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const mainRef = useRef(null);
  const location = useLocation();

  // Scroll main content to top on route change (sidebar stays in place)
  useEffect(() => {
    if (mainRef.current) {
      mainRef.current.scrollTo(0, 0);
    }
  }, [location.pathname]);

  const closeMobile = useCallback(() => setMobileOpen(false), []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-pulse flex flex-col items-center">
          <div className="w-12 h-12 rounded-full bg-slate-200 mb-4"></div>
          <div className="h-4 w-32 bg-slate-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="app-layout">
      {/* Mobile header */}
      <div className="mobile-header" data-testid="mobile-header">
        <div className="mobile-header-logo">
          <img src={LOGO_DARK_BG} alt="KR Enterprise Group" style={{ height: '48px', width: 'auto', maxWidth: '220px', objectFit: 'contain' }} />
        </div>
        <button
          className="mobile-hamburger"
          onClick={() => setMobileOpen(!mobileOpen)}
          data-testid="mobile-hamburger-btn"
          aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
        >
          {mobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile overlay */}
      <div
        className={`mobile-overlay ${mobileOpen ? 'visible' : ''}`}
        onClick={closeMobile}
        data-testid="mobile-overlay"
      />

      <Sidebar isOpen={mobileOpen} onClose={closeMobile} />
      <main className="main-content" ref={mainRef}>
        <CalendarReconnectBanner />
        <ImpersonationBar />
        <Outlet />
      </main>
      <SeasonalAccents />
      <ForcePasswordChangeModal />
      <UrgentTaskGate />
      <Softphone />
    </div>
  );
};

export default MainLayout;
